// Slice 46 — Overview dashboard. Pure orchestrator over the existing
// per-tab APIs; one `/admin/overview` call hydrates the entire screen.
// Cross-screen nav (banner → Privacy, KPI count → that screen) lives in
// the SPA — the URL hash is the routing surface.

import { useEffect, useMemo, useState } from "react";
import { OverviewApi, type OverviewData } from "../../api/client";
import { useRouter } from "../../router/router";
import { useShell } from "../../shell/HqShellContext";
import { HqBadge, HqButton, HqCard, HqStatusDot, Icons } from "../../ui";

const ACTION_TONE: Record<string, "accent" | "danger" | "warn" | "success" | "neutral"> = {
  "license.issue": "accent",
  "license.revoke": "danger",
  "license.unrevoke": "warn",
  "feedback.update": "neutral",
  "feedback.reply": "accent",
  "feedback.redact": "warn",
  "customer.create": "success",
  "customer.update": "neutral",
  "customer.reveal_email": "warn",
  "customer.export": "accent",
  "customer.erase": "danger",
  "dsr.open": "warn",
  "dsr.verify": "accent",
  "dsr.fulfil": "success",
  "dsr.reject": "danger",
  "retention.update": "neutral",
  "subprocessor.add": "success",
  "subprocessor.remove": "danger",
  "auth.bootstrap": "accent",
  "auth.login": "neutral",
  "auth.logout": "neutral",
};

export function OverviewScreen() {
  const { refreshTick, username } = useShell();
  const { navigate } = useRouter();
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void refreshTick;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const res = await OverviewApi.get();
      if (cancelled) return;
      setLoading(false);
      if (res.ok) setData(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  if (!data) {
    return (
      <HqCard title="Overview" icon={Icons.Activity}>
        <p
          style={{
            margin: 0,
            color: "var(--hq-text-dim)",
            fontSize: 13,
            textAlign: "center",
            padding: "16px 0",
          }}
        >
          {loading ? "Loading…" : "Failed to load."}
        </p>
      </HqCard>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Greeting username={username} weekday={data.weekday} />
      {data.pendingDsr.count > 0 && (
        <PendingDsrBanner pending={data.pendingDsr} onJump={() => navigate("privacy")} />
      )}
      <KpiStrip kpi={data.kpi} onNav={navigate} />
      <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 16 }}>
        <ActivationsChart activations={data.activations} />
        <Funnel funnel={data.funnel} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 16 }}>
        <RecentFeedback rows={data.recentFeedback} onJump={() => navigate("feedback")} />
        <RecentActivity rows={data.recentActivity} onJump={() => navigate("audit")} />
      </div>
    </div>
  );
}

function Greeting({ username, weekday }: { username: string; weekday: string }) {
  const hour = new Date().getHours();
  const period =
    hour < 5
      ? "Up late"
      : hour < 12
        ? "Good morning"
        : hour < 18
          ? "Good afternoon"
          : "Good evening";
  const today = new Date();
  const day = today.getUTCDate();
  const month = today.toLocaleString("en-US", { month: "long" });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span
        className="hq-mono"
        style={{
          fontSize: 10,
          color: "var(--hq-text-dim)",
          letterSpacing: ".15em",
          textTransform: "uppercase",
        }}
      >
        Vyvern HQ · {weekday}, {day} {month}
      </span>
      <h1
        style={{
          margin: 0,
          fontSize: 24,
          fontWeight: 600,
          letterSpacing: "-.02em",
          color: "var(--hq-text)",
          lineHeight: 1.2,
        }}
      >
        {period}, {username.charAt(0).toUpperCase() + username.slice(1)}.
      </h1>
    </div>
  );
}

function PendingDsrBanner({
  pending,
  onJump,
}: {
  pending: { count: number; dangerCount: number };
  onJump: () => void;
}) {
  const tone = pending.dangerCount > 0 ? "danger" : "warn";
  const color = tone === "danger" ? "var(--hq-danger)" : "var(--hq-warn)";
  const bg = tone === "danger" ? "var(--hq-danger-soft)" : "var(--hq-warn-soft)";
  return (
    <div
      style={{
        padding: 14,
        background: bg,
        border: `1px solid ${tone === "danger" ? "rgba(251,113,133,.35)" : "rgba(251,191,36,.35)"}`,
        borderRadius: "var(--hq-radius-lg)",
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}
    >
      <Icons.Shield width={18} height={18} sw={1.8} style={{ color }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color }}>
          {pending.count} DSR{pending.count === 1 ? "" : "s"} past warn-at
        </div>
        {pending.dangerCount > 0 && (
          <div style={{ fontSize: 11.5, color: "var(--hq-danger)", marginTop: 2 }}>
            {pending.dangerCount} within 3 days of the statutory deadline.
          </div>
        )}
      </div>
      <HqButton variant="ghost" size="sm" onClick={onJump}>
        Open queue →
      </HqButton>
    </div>
  );
}

function KpiStrip({
  kpi,
  onNav,
}: {
  kpi: OverviewData["kpi"];
  onNav: (id: "licenses" | "feedback" | "customers" | "privacy") => void;
}) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
      <Kpi
        label="Active licenses"
        value={kpi.activeLicenses.toString()}
        onClick={() => onNav("licenses")}
      />
      <Kpi
        label="New feedback"
        value={kpi.newFeedback.toString()}
        tone="warn"
        onClick={() => onNav("feedback")}
      />
      <Kpi label="Beta cohort" value={kpi.betaCohort.toString()} />
      <Kpi
        label="Customers"
        value={kpi.totalCustomers.toString()}
        onClick={() => onNav("customers")}
      />
      <Kpi
        label="Open DSRs"
        value={kpi.openDsr.toString()}
        tone={kpi.openDsr > 0 ? "warn" : "success"}
        onClick={() => onNav("privacy")}
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  tone,
  onClick,
}: {
  label: string;
  value: string;
  tone?: "warn" | "success" | "danger";
  onClick?: () => void;
}) {
  const color =
    tone === "danger"
      ? "var(--hq-danger)"
      : tone === "warn"
        ? "var(--hq-warn)"
        : tone === "success"
          ? "var(--hq-success)"
          : "var(--hq-text)";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      style={{
        padding: 16,
        background: "var(--hq-bg-raised)",
        borderRadius: "var(--hq-radius-2xl)",
        border: "1px solid var(--hq-border)",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        textAlign: "left",
        cursor: onClick ? "pointer" : "default",
        transition: "border-color .15s",
      }}
      onMouseEnter={(e) => {
        if (onClick)
          (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(34,211,238,.35)";
      }}
      onMouseLeave={(e) => {
        if (onClick) (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--hq-border)";
      }}
    >
      <span
        className="hq-mono"
        style={{
          fontSize: 10,
          color: "var(--hq-text-dim)",
          letterSpacing: ".1em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span
        style={{ fontSize: 26, fontWeight: 600, color, lineHeight: 1, letterSpacing: "-.02em" }}
      >
        {value}
      </span>
    </button>
  );
}

function ActivationsChart({ activations }: { activations: OverviewData["activations"] }) {
  const max = useMemo(
    () => Math.max(1, ...activations.series.map((p) => p.count)),
    [activations.series],
  );
  const W = 600;
  const H = 140;
  const padX = 8;
  const padY = 16;
  const innerW = W - padX * 2;
  const innerH = H - padY * 2;
  const step = innerW / Math.max(activations.series.length - 1, 1);

  const points = activations.series.map((p, i) => {
    const x = padX + i * step;
    const y = padY + innerH - (p.count / max) * innerH;
    return { x, y, count: p.count, date: p.date };
  });

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(" ");
  const areaPath = `${linePath} L${padX + innerW},${padY + innerH} L${padX},${padY + innerH} Z`;

  return (
    <HqCard
      title="Activations · 30 days"
      icon={Icons.Activity}
      right={
        <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-accent)" }}>
          {activations.totalLast30} total
        </span>
      }
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: H, color: "var(--hq-accent)", overflow: "visible" }}
        role="img"
        aria-label="30-day activations chart"
      >
        <title>30-day activations</title>
        <defs>
          <linearGradient id="actGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity=".35" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#actGrad)" />
        <path
          d={linePath}
          stroke="currentColor"
          strokeWidth="1.6"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ filter: "drop-shadow(0 0 3px rgba(34,211,238,.55))" }}
        />
        {points[points.length - 1] && (
          <circle
            cx={points[points.length - 1]?.x}
            cy={points[points.length - 1]?.y}
            r="3.5"
            fill="currentColor"
            style={{ filter: "drop-shadow(0 0 4px rgba(34,211,238,.7))" }}
          />
        )}
      </svg>
      <div
        style={{
          marginTop: 8,
          display: "flex",
          justifyContent: "space-between",
          fontFamily: "var(--hq-font-mono)",
          fontSize: 10,
          color: "var(--hq-text-dim)",
        }}
      >
        <span>{activations.series[0]?.date}</span>
        <span>{activations.series[activations.series.length - 1]?.date}</span>
      </div>
    </HqCard>
  );
}

function Funnel({ funnel }: { funnel: OverviewData["funnel"] }) {
  const max = funnel[0]?.count ?? 1;
  return (
    <HqCard title="Funnel · 30d" icon={Icons.Telemetry}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {funnel.map((step, i) => {
          const pct = max > 0 ? (step.count / max) * 100 : 0;
          const prev = i === 0 ? null : funnel[i - 1];
          const dropRate =
            prev && prev.count > 0 ? Math.round(100 * (1 - step.count / prev.count)) : null;
          const color =
            i === 0
              ? "var(--hq-accent)"
              : i === funnel.length - 1
                ? "var(--hq-success)"
                : "var(--hq-tier-pro)";
          return (
            <div key={step.key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 11.5, color: "var(--hq-text-muted)", flex: 1 }}>
                  {step.label}
                </span>
                <span
                  className="hq-mono"
                  style={{ fontSize: 11, color: "var(--hq-text)", fontWeight: 600 }}
                >
                  {step.count}
                </span>
                {dropRate != null && dropRate > 0 && (
                  <span className="hq-mono" style={{ fontSize: 10, color: "var(--hq-text-dim)" }}>
                    −{dropRate}%
                  </span>
                )}
              </div>
              <div
                style={{
                  height: 6,
                  width: "100%",
                  background: "var(--hq-bg-input)",
                  borderRadius: 99,
                  overflow: "hidden",
                }}
              >
                <div style={{ width: `${Math.max(2, pct)}%`, height: "100%", background: color }} />
              </div>
            </div>
          );
        })}
      </div>
    </HqCard>
  );
}

function RecentFeedback({
  rows,
  onJump,
}: {
  rows: OverviewData["recentFeedback"];
  onJump: () => void;
}) {
  return (
    <HqCard
      title={`New feedback · ${rows.length} unread`}
      icon={Icons.Inbox}
      right={
        <HqButton size="sm" variant="ghost" onClick={onJump}>
          Open inbox →
        </HqButton>
      }
    >
      {rows.length === 0 ? (
        <p
          style={{
            margin: 0,
            color: "var(--hq-text-dim)",
            fontSize: 12.5,
            textAlign: "center",
            padding: "8px 0",
          }}
        >
          Nothing new.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map((r) => (
            <div
              key={r.id}
              style={{
                padding: 10,
                background: "var(--hq-bg-input)",
                border: "1px solid var(--hq-border-input)",
                borderRadius: "var(--hq-radius-md)",
                display: "flex",
                gap: 10,
              }}
            >
              <HqStatusDot tone="accent" pulse size={6} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="hq-mono" style={{ fontSize: 10.5, color: "var(--hq-text)" }}>
                    {r.licenseId ?? "anon"}
                  </span>
                  <HqBadge tone="neutral" size="sm">
                    v{r.vyvernVersion}
                  </HqBadge>
                  <span style={{ flex: 1 }} />
                  <span className="hq-mono" style={{ fontSize: 10, color: "var(--hq-text-dim)" }}>
                    {relative(r.receivedAt)}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 11.5,
                    color: "var(--hq-text-muted)",
                    marginTop: 4,
                    lineHeight: 1.4,
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {r.body}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </HqCard>
  );
}

function RecentActivity({
  rows,
  onJump,
}: {
  rows: OverviewData["recentActivity"];
  onJump: () => void;
}) {
  return (
    <HqCard
      title="Recent activity"
      icon={Icons.Audit}
      right={
        <HqButton size="sm" variant="ghost" onClick={onJump}>
          Full log →
        </HqButton>
      }
    >
      {rows.length === 0 ? (
        <p
          style={{
            margin: 0,
            color: "var(--hq-text-dim)",
            fontSize: 12.5,
            textAlign: "center",
            padding: "8px 0",
          }}
        >
          No activity yet.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {rows.map((r) => (
            <ActivityRow key={r.id} row={r} />
          ))}
        </div>
      )}
    </HqCard>
  );
}

function ActivityRow({ row }: { row: OverviewData["recentActivity"][number] }) {
  const tone = ACTION_TONE[row.action] ?? "neutral";
  return (
    <div
      style={{
        padding: "6px 10px",
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 11.5,
        borderBottom: "1px solid var(--hq-border)",
      }}
    >
      <HqBadge tone={tone} size="sm">
        {row.action}
      </HqBadge>
      <span
        style={{
          color: "var(--hq-text-muted)",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {row.actor} {row.targetType}
        {row.targetId && (
          <span className="hq-mono" style={{ color: "var(--hq-text-dim)", marginLeft: 4 }}>
            {row.targetId.slice(0, 14)}…
          </span>
        )}
      </span>
      <span className="hq-mono" style={{ fontSize: 10, color: "var(--hq-text-dim)" }}>
        {relative(row.at)}
      </span>
    </div>
  );
}

function relative(ms: number): string {
  const diff = Math.max(0, Date.now() - ms);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "now";
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}
